import { isBefore } from 'date-fns'
import { hubRequest, hubRoutes } from '@/utilities'
import { fuelSurchargeAmount } from '@/utilities/constants'

/**
 * Determines the ecommerce discount based on dumpster size
 * @param {number|string} size - Dumpster size in yards
 * @returns {object|null} - Discount details or null if no discount applies
 */

export default {
  namespaced: true,
  state: () => ({
    item: null,
    brand: process.env.VUE_APP_BRAND,
    savedCartUUID: '',
    savedCartCreatedAt: '',
    savedCartUtmSource: '',
    savedCartUtmMedium: '',
    savedCartUtmCampaign: '',
    abandonedLeadSent: false,
    ppcLeadUUID: null,
    discountCode: '',
    discountCodeValid: null,
    discountAmount: 0,
    discountName: '',
    preservedDiscountAmount: 0 // Preserve last valid discount when invalid code is entered
  }),
  actions: {
    clearSavedCart ({ state }) {
      state.savedCartUUID = ''
      state.savedCartCreatedAt = ''
      state.savedCartUtmSource = ''
      state.savedCartUtmMedium = ''
      state.savedCartUtmCampaign = ''
    },
    createLead ({ commit, state, getters, rootState }, payload) {
      const { abandonedCartFlag } = payload
      if (abandonedCartFlag) { state.abandonedLeadSent = true }
      return hubRequest(hubRoutes.CREATE_LEAD, { ...getters.baseSFPayload, ...payload })
        .then(res => {
          commit('ppcLeadUUID', res.data.id)
        })
    },
    updateLead ({ commit, state, getters, rootState }, payload) {
      return hubRequest(hubRoutes.UPDATE_LEAD, { ...getters.baseSFPayload, ...payload })
    },
    /**
     * Skips a duplicate hub call when the same code is already stored and still valid.
     */
    applyDiscountCode ({ dispatch, commit, state }, { code } = {}) {
      const trimmed = code == null ? '' : String(code).trim()
      if (!trimmed) return Promise.resolve()
      const sameAsStore = state.discountCode === trimmed.toUpperCase()
      commit('discountCode', trimmed)
      if (!sameAsStore || state.discountCodeValid !== true) {
        return dispatch('getDiscount', { useCartItem: !!state.item })
      }
      return Promise.resolve()
    },
    // Single action for discount validation - works for both browsing and cart
    getDiscount ({ commit, state, rootState }, { useCartItem = false } = {}) {
      const { discountCode: code, item } = state

      // For cart validation, require cart item
      if (useCartItem && !item) {
        commit('discountCodeValid', false)
        return Promise.resolve()
      }

      const upperCode = code?.toUpperCase() || ''
      if (!upperCode) {
        commit('discountCodeValid', false)
        return Promise.resolve()
      }

      // Get size and price based on context
      const price = useCartItem
        ? Number(item?.price) || 0
        : Number(rootState.dumpster.selectedDumpster?.price) || 0

      const clearPromotionAndCode = () => {
        if (useCartItem && state.preservedDiscountAmount === 0) {
          if (state.discountCodeValid === true && state.discountAmount > 0) {
            commit('preservedDiscountAmount', state.discountAmount)
          }
        }
        commit('discountCode', '')
        commit('discountCodeValid', false)
        commit('discountAmount', 0)
        commit('discountName', '')
      }

      const markPromotionInvalidFromHub = () => {
        commit('discountCodeValid', false)
        commit('discountAmount', 0)
        commit('discountName', '')
      }

      // Helper function to apply valid discount with price validation
      const applyDiscount = (amount, name) => {
        const numericAmount = Number(amount) || 0
        // If price is not ready yet, just store discount
        if (price == null || price <= 0) {
          commit('discountCodeValid', true)
          commit('discountAmount', numericAmount)
          commit('discountName', name || '')
          return
        }

        if ((price - numericAmount) < 0) {
          clearPromotionAndCode()
          return
        }
        // Clear preserved amount when applying new valid discount (replacing existing)
        commit('preservedDiscountAmount', 0)
        commit('discountCodeValid', true)
        commit('discountAmount', numericAmount)
        commit('discountName', name || '')
      }

      return hubRequest(hubRoutes.DISCOUNT_CODE, { code }, 'get')
        .then(res => {
          const discountApiData = res?.data ?? {}
          const numericAmount = Number(discountApiData.amount) || 0
          if (!numericAmount) {
            markPromotionInvalidFromHub()
          } else {
            const name = discountApiData.name || discountApiData.discountName || ''
            applyDiscount(numericAmount, name)
          }
        })
        .catch(error => {
          markPromotionInvalidFromHub()
          console.log('Error on call to /api/discountCode', error)
        })
    }
  },
  getters: {
    baseSFPayload (state, getters, rootState, rootGetters) {
      const {
        discountCode,
        discountName,
        savedCartCreatedAt,
        savedCartUtmCampaign,
        savedCartUtmMedium,
        savedCartUtmSource
      } = state
      const appliedDiscountAmount = getters.cartDiscountAmount
      return {
        brand: process.env.VUE_APP_BRAND,
        cart: state,
        ccType: '',
        customer: {
          ...rootState.customer,
          deliveryDateMilliseconds: rootGetters['customer/deliveryDateMilliseconds']
        },
        discountAmount: appliedDiscountAmount,
        discountCode,
        discountName,
        mapping: null,
        onPoint: rootState.onPoint.savedLocationResponse,
        payment: {},
        project: rootState.project,
        savedCartCreatedAt,
        savedCartUtmCampaign,
        savedCartUtmMedium,
        savedCartUtmSource,
        sfid: state.ppcLeadUUID,
        // Send the computed surcharge separately so Salesforce fulfillment can add it manually.
        fuelSurcharge: getters.fuelSurcharge,
        taxAmount: getters.taxAmount,
        totalAmount: getters.totalAmount,
        UUID: state.savedCartUUID
      }
    },
    datesUnavailable (state, getters, rootState) {
      const datesUnavailable = state.item.datesUnavailable.map(date => {
        return new Date(`${date}T00:00:00`)
      })
      // After July 2 2026 at 1pm Eastern time, block July 6 2026
      const nowEastern = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
      if (nowEastern >= new Date('2026-07-02T13:00:00')) {
        datesUnavailable.push(new Date('2026-07-06T00:00:00'))
      }

      return [...datesUnavailable, ...rootState.dumpster.holidays]
    },
    minDeliveryDate (state, getters, rootState) {
      const deliveryDate = new Date(`${state.item.deliveryDate}T00:00:00`)
      const currentDate = new Date()
      currentDate.setHours(0, 0, 0, 0)
      const datesUnavailable = state.item.datesUnavailable.map(date => {
        return new Date(`${date}T00:00:00`)
      })

      const isDateUnavailable = () => {
        if ((isBefore(deliveryDate, currentDate))) {
          return true
        } else {
          return datesUnavailable.find(item => item.getTime() === deliveryDate.getTime())
        }
      }
      // if the selected product delivery date is less than or equal to today, add a day
      while (isDateUnavailable()) {
        deliveryDate.setDate(deliveryDate.getDate() + 1)
      }
      return deliveryDate
    },
    // Get discount amount for calculations (respects validation state)
    cartDiscountAmount (state, getters) {
      if (state.discountCodeValid === true && state.discountAmount > 0) {
        return state.discountAmount
      }
      if (state.discountCodeValid === false && state.preservedDiscountAmount > 0) {
        return state.preservedDiscountAmount
      }
      return 0
    },
    // Derive the surcharge from the cart so rerenders and reloads cannot duplicate it.
    fuelSurcharge (state) {
      return state.item ? fuelSurchargeAmount : 0
    },
    taxAmount (state, getters, rootState) {
      if (!state.item || !rootState.customer?.serviceAddress) return 0
      const { item } = state
      const cartDiscount = getters.cartDiscountAmount
      // If it has a flat tax use as is
      if (item.hasFlatTax) return item.taxAmount.toFixed(2)
      // Calculate tax on discounted price (for ALL states)
      const taxableBase = cartDiscount > 0 && Number(item.price) > 0 && cartDiscount <= Number(item.price)
        ? Number(item.price) - cartDiscount + getters.fuelSurcharge
        : Number(item.price) + getters.fuelSurcharge
      return (taxableBase * (item.taxRate / 100)).toFixed(2)
    },
    totalAmount (state, getters, rootState) {
      if (!state.item || !rootState.customer?.serviceAddress) return 0
      const { item } = state
      const cartDiscount = getters.cartDiscountAmount
      const validDiscount = cartDiscount > 0 && cartDiscount <= Number(item.price) ? cartDiscount : 0
      // Total includes the one-time surcharge before tax is added.
      return (Number(item.price) - validDiscount + getters.fuelSurcharge + Number(getters.taxAmount)).toFixed(2)
    },
    /** Promo code in cart (Payment.vue / saved flows); mirrors store discountCode */
    cartDiscountCode (state) {
      return state.discountCode || ''
    }
  },
  mutations: {
    item (state, val) {
      state.item = val
    },
    discountCode (state, val) {
      state.discountCode = val == null ? '' : String(val).toUpperCase() // Codes are stored in postgres in ALLCAPS, this allows for case-insensitivity
    },
    discountCodeValid (state, val) {
      state.discountCodeValid = val
    },
    discountAmount (state, val) {
      state.discountAmount = val
    },
    preservedDiscountAmount (state, val) {
      state.preservedDiscountAmount = val
    },
    discountName (state, val) {
      state.discountName = val
    },
    ppcLeadUUID (state, val) {
      state.ppcLeadUUID = val
    },
    abandonedLeadSent (state, val) {
      state.abandonedLeadSent = val
    },
    savedCartUUID (state, val) {
      state.savedCartUUID = val
    },
    savedCartCreatedAt (state, val) {
      state.savedCartCreatedAt = val
    },
    savedCartUtmSource (state, val) {
      state.savedCartUtmSource = val
    },
    savedCartUtmMedium (state, val) {
      state.savedCartUtmMedium = val
    },
    savedCartUtmCampaign (state, val) {
      state.savedCartUtmCampaign = val
    }
  }
}
